import React, { useMemo, useState, useEffect } from 'react';
import { debounce } from 'lodash';
import * as R from 'ramda';
import DisabledUserActionsCell from './DisabledUserActionsCell';
import useFetchCompanyUsers from 'App/hooks/useFetchCompanyUsers';
import createUserLinkCell from '../../components/UserLinkCell/createUserLinkCell';
import propOrDash from 'App/utils/propOrDash';
import { USER_LINK_PATHS } from '../../constants';
import ManualPageTable from 'App/components/ManualPageTable/ManualPageTable';
import hasLength from 'App/utils/hasLength';
import SearchColumnFilterManual from 'App/components/ManualPageTable/SearchColumnFilterManual';

const propTypes = {};

const defaultProps = {};

function DisabledUsersTable() {
    const [query, setQuery] = useState({
        page: 1,
        count: 10,
        sortKey: 'firstName',
        sortOrder: 'asc',
        disabledOnly: true,
        search: {
            firstName: '',
            lastName: '',
            email: '',
        },
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
    const debouncedOnTextChange = useMemo(
        () =>
            debounce((name, value) => {
                setQuery((prevQuery) => ({
                    ...prevQuery,
                    page: 1,
                    search: {
                        ...prevQuery.search,
                        [name]: value,
                    },
                }));
            }, 500),
        []
    );

    useEffect(() => {
        return () => {
            debouncedOnTextChange.cancel();
        };
    }, [debouncedOnTextChange]);

    const columns = useMemo(
        () => [
            {
                Header: 'First Name',
                accessor: propOrDash('firstName'),
                id: 'firstName',
                Cell: createUserLinkCell(USER_LINK_PATHS.disabled.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="firstName"
                        placeHolder="Filter by First Name"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Last Name',
                accessor: propOrDash('lastName'),
                id: 'lastName',
                Cell: createUserLinkCell(USER_LINK_PATHS.disabled.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="lastName"
                        placeHolder="Filter by Last Name"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Email',
                accessor: propOrDash('email'),
                id: 'email',
                Cell: createUserLinkCell(USER_LINK_PATHS.disabled.key),
                canSort: true,
                Filter: (
                    <SearchColumnFilterManual
                        name="email"
                        placeHolder="Filter by Email"
                        onFilter={(e) => debouncedOnTextChange(e.target.name, e.target.value)}
                    />
                ),
                canFilter: true,
            },
            {
                Header: 'Actions',
                id: 'actions',
                Cell: DisabledUserActionsCell,
            },
        ],
        // eslint-disable-next-line react-hooks/exhaustive-deps
        []
    );

    const { isLoading, data: { data, count } = {} } = useFetchCompanyUsers(query);

    const canSort = hasLength(data);

    const onPageChange = ({ pageIndex, pageSize }) => {
        setQuery((prevQuery) => ({
            ...prevQuery,
            page: pageIndex + 1,
            count: pageSize,
        }));
    };

    const onSortChange = (sort) => {
        setQuery((prevQuery) => ({
            ...prevQuery,
            sortKey: sort.sortKey,
            sortOrder: sort.sortOrder,
        }));
    };

    return (
        <ManualPageTable
            columns={columns}
            totalRows={count}
            canSort={canSort}
            data={data}
            isLoading={isLoading}
            pageSizeOptions={[10, 25, 50]}
            onPageChange={onPageChange}
            onSortChange={onSortChange}
            currentPageIndex={query.count}
            currentPage={query.page}
            getKey={R.prop('id')}
            initialSortKey="firstName"
            initialSortOrder="asc"
        />
    );
}

DisabledUsersTable.propTypes = propTypes;
DisabledUsersTable.defaultProps = defaultProps;

export default DisabledUsersTable;
